import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { Shield, RefreshCw, Download } from 'lucide-react';
import { useAuth, formatApiError } from '../context/AuthContext';
import { useAllFeatureFlags } from '../context/FeatureFlagsContext';
import DeveloperSettings from '../components/DeveloperSettings';
import AdminKpiAndCohort from '../components/Admin/AdminKpiAndCohort';
import ExerciseSuggestionsCard from '../components/Admin/ExerciseSuggestionsCard';
import FeatureFlagsCard from '../components/Admin/FeatureFlagsCard';
import SponsorshipInbox from '../components/Admin/SponsorshipInbox';
import FeedbackInbox from '../components/Admin/FeedbackInbox';
import InviteCodesCard from '../components/Admin/InviteCodesCard';
import AddTeamMemberCard from '../components/Admin/AddTeamMemberCard';
import RecentSignupsCard from '../components/Admin/RecentSignupsCard';
import DeleteUserModal from '../components/Admin/DeleteUserModal';
import BulkDeleteCard from '../components/Admin/BulkDeleteCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin — orchestration layer. Owns all state, handlers, and data
 * loading. Renders each section as its own component from
 * `components/Admin/`. Refactored from a 972-line monolith;
 * behavior preserved verbatim.
 */
const Admin = () => {
  const { user, loading, refresh } = useAuth();
  const [stats, setStats] = useState(null);
  const [codes, setCodes] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState('');

  // New invite code form state
  const [newCode, setNewCode] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newMaxUses, setNewMaxUses] = useState(10);
  const [newTierGrant, setNewTierGrant] = useState('egnite');

  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState('');

  // New team member form state
  const [tmEmail, setTmEmail] = useState('');
  const [tmName, setTmName] = useState('');
  const [tmPassword, setTmPassword] = useState('');
  const [tmRole, setTmRole] = useState('admin');
  const [tmCohort, setTmCohort] = useState('staff');
  const [tmBusy, setTmBusy] = useState(false);
  const [tmSuccess, setTmSuccess] = useState('');

  // Search / filter state for Recent signups
  const [searchQ, setSearchQ] = useState('');
  const [filterCohort, setFilterCohort] = useState('all');
  const [filterRole, setFilterRole] = useState('all');

  // Delete confirmation modal state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteTypeMatch, setDeleteTypeMatch] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Custom exercise suggestions queue
  const [suggestions, setSuggestions] = useState([]);
  const [reviewBusy, setReviewBusy] = useState(null);

  // Sponsorship applications inbox
  const [sponsorships, setSponsorships] = useState([]);

  // Beta feedback inbox (in-app FeedbackWidget submissions)
  const [feedback, setFeedback] = useState([]);
  const [feedbackBusyId, setFeedbackBusyId] = useState(null);

  // Feature flags (server-controlled runtime toggles)
  const { flags: featureFlags, refresh: refreshFlags } = useAllFeatureFlags();
  const [flagBusy, setFlagBusy] = useState(null);
  const [flagError, setFlagError] = useState('');

  const loadAll = async () => {
    setStatsLoading(true);
    setError('');
    try {
      const [statsRes, codesRes, suggestionsRes, sponsorshipsRes, feedbackRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/invite-codes`),
        axios.get(`${API}/admin/exercise-suggestions`),
        axios.get(`${API}/admin/sponsorship/applications`),
        axios.get(`${API}/admin/feedback`),
      ]);
      setStats(statsRes.data);
      setCodes(codesRes.data.codes || []);
      setSuggestions(Array.isArray(suggestionsRes.data) ? suggestionsRes.data : []);
      setSponsorships(sponsorshipsRes.data?.applications || []);
      setFeedback(feedbackRes.data?.items || []);
    } catch (err) {
      const status = err?.response?.status;
      const detail = formatApiError(err?.response?.data?.detail);
      if (status === 401) {
        setError('Session expired or missing — log out, then sign in again with your admin account.');
      } else if (status === 403) {
        setError('Admin access required. Use the account that matches Render ADMIN_EMAIL.');
      } else {
        setError(detail || err?.message || 'Failed to load admin data.');
      }
    } finally {
      setStatsLoading(false);
    }
  };

  const toggleFeatureFlag = async (flagId, nextEnabled) => {
    if (flagBusy === flagId) return;
    setFlagBusy(flagId);
    setFlagError('');
    try {
      await axios.patch(`${API}/admin/feature-flags/${flagId}`, { enabled: nextEnabled });
      await refreshFlags();
    } catch (err) {
      setFlagError(err?.response?.data?.detail || err?.message || 'Could not save toggle.');
    } finally {
      setFlagBusy(null);
    }
  };

  useEffect(() => {
    if (loading || user?.role !== 'admin') return;
    let cancelled = false;
    (async () => {
      try {
        const me = await refresh();
        if (cancelled) return;
        if (!me || me.role !== 'admin') {
          setError('Could not verify admin session. Log out and sign in again.');
          setStatsLoading(false);
          return;
        }
        await loadAll();
      } catch {
        if (!cancelled) {
          setError('Could not verify admin session. Log out and sign in again.');
          setStatsLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user?.role, loading, refresh]);

  // Cohort options derived from current data (must be above early returns — hook ordering)
  const cohortOptions = useMemo(() => {
    if (!stats) return [];
    const set = new Set(stats.cohorts.map((c) => c.cohort).filter(Boolean));
    return Array.from(set).sort();
  }, [stats]);

  // Filtered signups table
  const filteredSignups = useMemo(() => {
    const rows = stats?.recent_signups || [];
    const q = searchQ.trim().toLowerCase();
    return rows.filter((u) => {
      if (q) {
        const haystack = `${u.email} ${u.name || ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (filterCohort !== 'all' && (u.cohort || 'untagged') !== filterCohort) return false;
      if (filterRole !== 'all' && u.role !== filterRole) return false;
      return true;
    });
  }, [stats, searchQ, filterCohort, filterRole]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" state={{ from: '/admin' }} replace />;
  if (user.role !== 'admin') {
    return (
      <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-6 text-center py-24">
          <Shield className="w-8 h-8 text-enf-accent-fg mx-auto mb-3" />
          <h1 className="font-display text-5xl tracking-wide leading-none mb-3">Restricted.</h1>
          <p className="text-enf-text-secondary">This page is for Enferno admins only.</p>
        </div>
      </div>
    );
  }

  const createCode = async (e) => {
    e.preventDefault();
    if (!newCode.trim()) return;
    setCreating(true);
    setError('');
    try {
      await axios.post(`${API}/admin/invite-codes`, {
        code: newCode.trim().toUpperCase(),
        label: newLabel.trim() || 'untagged',
        max_uses: Number(newMaxUses) || 10,
        tier_grant: newTierGrant !== 'egnite' ? newTierGrant : null,
      });
      setNewCode('');
      setNewLabel('');
      setNewMaxUses(10);
      setNewTierGrant('egnite');
      await loadAll();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err?.message || 'Failed to create code.');
    } finally {
      setCreating(false);
    }
  };

  const deleteCode = async (code) => {
    if (!window.confirm(`Delete invite code ${code}?`)) return;
    try {
      await axios.delete(`${API}/admin/invite-codes/${code}`);
      await loadAll();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to delete.');
    }
  };

  const createTeamMember = async (e) => {
    e.preventDefault();
    if (!tmEmail.trim() || tmPassword.length < 8) return;
    setTmBusy(true);
    setError('');
    setTmSuccess('');
    try {
      const { data } = await axios.post(`${API}/admin/users`, {
        email: tmEmail.trim().toLowerCase(),
        password: tmPassword,
        name: tmName.trim(),
        role: tmRole,
        cohort: tmCohort.trim() || 'staff',
      });
      setTmSuccess(`Created ${data.email} as ${data.role}. They can sign in immediately at /login.`);
      setTmEmail('');
      setTmName('');
      setTmPassword('');
      setTmCohort('staff');
      setTmRole('admin');
      await loadAll();
      setTimeout(() => setTmSuccess(''), 6000);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to create user.');
    } finally {
      setTmBusy(false);
    }
  };

  const toggleRole = async (u) => {
    const newRole = u.role === 'admin' ? 'user' : 'admin';
    if (!window.confirm(`Change ${u.email}'s role from ${u.role} to ${newRole}?`)) return;
    try {
      await axios.patch(`${API}/admin/users/${u.id}/role`, { role: newRole });
      await loadAll();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to update role.');
    }
  };

  const changeTier = async (u, newTier) => {
    if (newTier === (u.subscription_tier || 'egnite')) return;
    // Don't allow setting to 'enferno' manually — that's Stripe-managed.
    const labels = { egnite: 'Egnite (free)', founder: 'Founder ($249 lifetime)', hero: 'Hero ($999 lifetime)', comp: 'Comp (complimentary)' };
    if (!window.confirm(`Change ${u.email}'s tier to ${labels[newTier]}?`)) return;
    try {
      await axios.patch(`${API}/admin/users/${u.id}/tier`, { tier: newTier });
      await loadAll();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to update tier.');
    }
  };

  const expireTrial = async (u) => {
    if (!window.confirm(
      `Expire ${u.email}'s 14-day signup trial?\n\n` +
      `This backdates their onboardingCompletedAt to 15 days ago so they fall ` +
      `through to true free-tier (egnite) state. Their lifetime/Stripe tier ` +
      `state is unchanged. Useful for QA-testing free-tier paywalls without ` +
      `creating a new account.`
    )) return;
    try {
      await axios.post(`${API}/admin/users/${u.id}/expire-trial`);
      await loadAll();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to expire trial.');
    }
  };

  const resetTrial = async (u) => {
    if (!window.confirm(
      `Reset ${u.email}'s 14-day signup trial?\n\n` +
      `This restarts their trial from now (14 days left) AND clears their AI ` +
      `usage so the trial-AI cap resets too. Lets you reuse this account for ` +
      `testing instead of creating a new one. Stripe/lifetime tier is unchanged.`
    )) return;
    try {
      await axios.post(`${API}/admin/users/${u.id}/reset-trial`);
      await loadAll();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to reset trial.');
    }
  };

  const openDeleteModal = (u) => {
    setDeleteTarget(u);
    setDeleteTypeMatch('');
  };

  const cancelDelete = () => {
    setDeleteTarget(null);
    setDeleteTypeMatch('');
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleteTypeMatch !== deleteTarget.email) return;
    setDeleteBusy(true);
    setError('');
    try {
      await axios.delete(`${API}/admin/users/${deleteTarget.id}`);
      setDeleteTarget(null);
      setDeleteTypeMatch('');
      await loadAll();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to delete user.');
    } finally {
      setDeleteBusy(false);
    }
  };

  const hasActiveFilters = searchQ || filterCohort !== 'all' || filterRole !== 'all';
  const clearFilters = () => { setSearchQ(''); setFilterCohort('all'); setFilterRole('all'); };

  const setFeedbackStatus = async (id, status) => {
    setFeedbackBusyId(id);
    try {
      await axios.patch(`${API}/admin/feedback/${id}`, { status });
      setFeedback((prev) => prev.map((f) => (f.id === id ? { ...f, status } : f)));
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err?.message || 'Failed to update feedback.');
    } finally {
      setFeedbackBusyId(null);
    }
  };

  const downloadCsv = async (kind) => {
    try {
      const res = await axios.get(`${API}/admin/export/${kind}.csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replaceAll('-', '');
      a.download = `enferno-${kind}-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Export failed.');
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(text);
      setTimeout(() => setCopied(''), 1500);
    } catch { /* ignore */ }
  };

  const inviteLink = (code) => `${window.location.origin}/register?invite=${encodeURIComponent(code)}`;

  const reviewSuggestion = async (id, status) => {
    setReviewBusy(id);
    setError('');
    try {
      await axios.post(`${API}/admin/exercise-suggestions/${id}/review`, { status });
      setSuggestions((arr) => arr.filter((s) => s.id !== id));
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to review suggestion.');
    } finally {
      setReviewBusy(null);
    }
  };

  const pendingSuggestions = suggestions.filter((s) => s.suggestion_status === 'pending' || !s.suggestion_status);

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-2 flex items-center gap-2">
              <Shield className="w-3.5 h-3.5" /> Admin · Founders only
            </div>
            <h1 className="font-display text-6xl md:text-7xl tracking-wide leading-none">Fire Station.</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadCsv('users')}
              className="px-4 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm flex items-center gap-2 transition-colors"
              data-testid="admin-export-users"
            >
              <Download className="w-4 h-4" /> Export users
            </button>
            <button
              onClick={loadAll}
              className="px-4 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
              disabled={statsLoading}
              data-testid="admin-refresh">
              <RefreshCw className={`w-4 h-4 ${statsLoading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 px-4 py-3 bg-enf-accent/10 border border-enf-accent-fg/40 rounded-md text-sm text-enf-accent-fg">{error}</div>
        )}

        <AdminKpiAndCohort stats={stats} />

        <ExerciseSuggestionsCard
          pendingSuggestions={pendingSuggestions}
          reviewBusy={reviewBusy}
          onReview={reviewSuggestion}
        />

        <FeatureFlagsCard
          featureFlags={featureFlags}
          flagBusy={flagBusy}
          flagError={flagError}
          onToggle={toggleFeatureFlag}
        />

        {/* Developer tools — moved from Profile, admin-only by virtue of being on /admin */}
        <div className="mb-10" data-testid="admin-developer-card">
          <DeveloperSettings />
        </div>

        {/* Library image manifest — visual review tool */}
        <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6 mb-10" data-testid="admin-image-manifest-card">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary mb-1">Library imagery</div>
              <p className="text-xs text-enf-text-muted leading-relaxed max-w-2xl">
                Review every candidate exercise image (NanoBanana set + your recent uploads) on a single visual page. Mark keep / kill, then apply.
              </p>
            </div>
            <a
              href="/admin/image-manifest"
              className="inline-flex items-center gap-2 px-4 py-2 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors flex-shrink-0"
              data-testid="open-image-manifest"
            >
              Open manifest
            </a>
          </div>
        </div>

        <SponsorshipInbox sponsorships={sponsorships} />

        <FeedbackInbox
          feedback={feedback}
          onSetStatus={setFeedbackStatus}
          busyId={feedbackBusyId}
        />

        <InviteCodesCard
          codes={codes}
          newCode={newCode} setNewCode={setNewCode}
          newLabel={newLabel} setNewLabel={setNewLabel}
          newMaxUses={newMaxUses} setNewMaxUses={setNewMaxUses}
          newTierGrant={newTierGrant} setNewTierGrant={setNewTierGrant}
          creating={creating}
          onCreate={createCode}
          onDelete={deleteCode}
          copied={copied}
          onCopy={copy}
          inviteLink={inviteLink}
        />

        <AddTeamMemberCard
          tmEmail={tmEmail} setTmEmail={setTmEmail}
          tmName={tmName} setTmName={setTmName}
          tmPassword={tmPassword} setTmPassword={setTmPassword}
          tmRole={tmRole} setTmRole={setTmRole}
          tmCohort={tmCohort} setTmCohort={setTmCohort}
          tmBusy={tmBusy}
          tmSuccess={tmSuccess}
          onCreate={createTeamMember}
        />

        <BulkDeleteCard onAfterDelete={loadAll} />

        <RecentSignupsCard
          stats={stats}
          filteredSignups={filteredSignups}
          hasActiveFilters={hasActiveFilters}
          searchQ={searchQ} setSearchQ={setSearchQ}
          filterCohort={filterCohort} setFilterCohort={setFilterCohort}
          filterRole={filterRole} setFilterRole={setFilterRole}
          cohortOptions={cohortOptions}
          onClearFilters={clearFilters}
          onToggleRole={toggleRole}
          onChangeTier={changeTier}
          onExpireTrial={expireTrial}
          onResetTrial={resetTrial}
          onOpenDelete={openDeleteModal}
        />
      </div>

      <DeleteUserModal
        target={deleteTarget}
        typeMatch={deleteTypeMatch} setTypeMatch={setDeleteTypeMatch}
        busy={deleteBusy}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default Admin;
